import { useMemo, useState } from "react";
import { TabHeader } from "components/TabHeader";
import { Button } from "newui/components/Button";
import { InputWithLabel } from "newui/components/ui/InputWithLabel";
import { DateRangeFilter, DateRangeValue } from "newui/components/Filters/DateRangeFilter";
import { TableColumnFilter } from "newui/components/Filters/TableColumnFilter";
import { BaseErrorPage } from "newui/features/ErrorPages/components/BaseErrorPage";
import { DeliveryAuditTable } from "./components/DeliveryAuditTable";
import { DeliveryAuditExportButton } from "./components/DeliveryAuditExportButton";
import { useDeliveryAudit } from "./hooks/useDeliveryAudit";
import { notificationsLang } from "./lang/en";
import type { NotificationDeliveryFilters, NotificationDeliveryStatus } from "./types/Notifications.type";

export interface NotificationDeliveryAuditPageProps {
	/** Path prop is consumed by @reach/router; not used directly. */
	path?: string;
}

const STATUS_OPTIONS: { label: string; value: NotificationDeliveryStatus }[] = [
	{ label: notificationsLang.auditStatusSent, value: "sent" },
	{ label: notificationsLang.auditStatusSuppressed, value: "suppressed" },
	{ label: notificationsLang.auditStatusFailed, value: "failed" },
];

const isPermissionDeniedError = (error: unknown): boolean => {
	const status = (error as { response?: { status?: number } } | undefined)?.response?.status;
	return status === 403;
};

export const NotificationDeliveryAuditPage = (_props: NotificationDeliveryAuditPageProps) => {
	const [user, setUser] = useState("");
	const [type, setType] = useState("");
	const [status, setStatus] = useState<NotificationDeliveryStatus[]>([]);
	const [context, setContext] = useState("");
	const [dateRange, setDateRange] = useState<DateRangeValue>({ from: null, to: null });
	const [page, setPage] = useState(1);

	const filters: NotificationDeliveryFilters = useMemo(
		() => ({
			user: user.trim() || undefined,
			type: type.trim() || undefined,
			status: status[0],
			context: context.trim() || undefined,
			from: dateRange.from ?? undefined,
			to: dateRange.to ?? undefined,
			page,
		}),
		[user, type, status, context, dateRange, page]
	);

	const { data, isLoading, isError, error } = useDeliveryAudit(filters);

	if (isError && isPermissionDeniedError(error)) {
		return (
			<BaseErrorPage
				errorCode={notificationsLang.auditPermissionDeniedCode}
				title={notificationsLang.auditPermissionDeniedTitle}
				message={notificationsLang.auditPermissionDeniedMessage}
			/>
		);
	}

	const deliveries = data?.data ?? [];
	const meta = data?.meta;

	return (
		<div data-testid="notification-delivery-audit-page">
			<TabHeader heading={notificationsLang.auditPageHeading} description={notificationsLang.auditPageDescription} />

			<div className="mt-5 flex flex-wrap items-end justify-between gap-3">
				<div className="flex flex-wrap items-end gap-3">
					<div className="w-48">
						<InputWithLabel
							label={notificationsLang.auditFilterUser}
							value={user}
							onChange={(e) => {
								setUser(e.target.value);
								setPage(1);
							}}
							testId="delivery-audit-user-filter"
						/>
					</div>
					<div className="w-48">
						<InputWithLabel
							label={notificationsLang.auditFilterType}
							value={type}
							onChange={(e) => {
								setType(e.target.value);
								setPage(1);
							}}
							testId="delivery-audit-type-filter"
						/>
					</div>
					<TableColumnFilter
						label={notificationsLang.auditFilterStatus}
						options={STATUS_OPTIONS}
						value={status}
						onChange={(value) => {
							setStatus(value);
							setPage(1);
						}}
					/>
					<div className="w-48">
						<InputWithLabel
							label={notificationsLang.auditFilterContext}
							value={context}
							onChange={(e) => {
								setContext(e.target.value);
								setPage(1);
							}}
							testId="delivery-audit-context-filter"
						/>
					</div>
					<DateRangeFilter
						label={notificationsLang.auditFilterDate}
						value={dateRange}
						onChange={(value) => {
							setDateRange(value);
							setPage(1);
						}}
					/>
				</div>

				<DeliveryAuditExportButton
					filters={{
						user: filters.user,
						type: filters.type,
						status: filters.status,
						context: filters.context,
						from: filters.from,
						to: filters.to,
					}}
				/>
			</div>

			<div className="mt-4">
				<DeliveryAuditTable deliveries={deliveries} isLoading={isLoading} />
			</div>

			{meta && meta.last_page > 1 && (
				<div className="mt-4 flex items-center justify-between text-sm text-gray-600">
					<span>
						Page {meta.current_page} of {meta.last_page} ({meta.total} deliveries)
					</span>
					<div className="flex gap-2">
						<Button
							btntype="secondary"
							size="sm"
							data-testid="delivery-audit-page-prev"
							disabled={meta.current_page <= 1}
							onClick={() => setPage((prev) => Math.max(1, prev - 1))}
						>
							Previous
						</Button>
						<Button
							btntype="secondary"
							size="sm"
							data-testid="delivery-audit-page-next"
							disabled={meta.current_page >= meta.last_page}
							onClick={() => setPage((prev) => Math.min(meta.last_page, prev + 1))}
						>
							Next
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};
